import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Zap } from "lucide-react";

export default function Login() {
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("admin@leadifycrm.com");
  const [password, setPassword] = useState("Admin@2026");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register({ company_name: company, name, email, password });
      }
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-[#0A0A0A] border-r border-border p-16 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-sm bg-primary flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-xl font-extrabold tracking-tight">LeadifyCRM</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold">Command Center</div>
            </div>
          </div>
        </div>
        <div className="relative space-y-8">
          <h1 className="font-display text-5xl lg:text-6xl font-extrabold leading-[0.95] tracking-tight">
            The revenue<br />operating system<br />
            <span className="text-primary">for teams that ship.</span>
          </h1>
          <p className="text-muted-foreground text-base max-w-md leading-relaxed">
            Unified inbox across email, chat, WhatsApp & socials. A qualification-first leads queue. Kanban pipelines you can drag. AI trained on your own docs. All under one roof.
          </p>
          <div className="grid grid-cols-3 gap-6 pt-4 border-t border-border/50">
            <Stat n="6" label="Integrated modules" />
            <Stat n="∞" label="Divisions" />
            <Stat n="24/7" label="AI chatbot" />
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground/60 font-bold">© 2026 LeadifyCRM</div>
      </div>

      {/* right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-16">
        <Card className="w-full max-w-md p-8 bg-card border-border rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-2">
            {mode === "login" ? "Sign in" : "Get started"}
          </div>
          <h2 className="font-display text-3xl font-extrabold tracking-tight mb-8">
            {mode === "login" ? "Welcome back." : "Create your workspace."}
          </h2>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label>Company name</Label>
                  <Input value={company} onChange={(e) => setCompany(e.target.value)} required data-testid={AUTH.companyInput} placeholder="Acme Inc." className="mt-1.5 rounded-sm bg-secondary/40" />
                </div>
                <div>
                  <Label>Your name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid={AUTH.nameInput} placeholder="Jane Doe" className="mt-1.5 rounded-sm bg-secondary/40" />
                </div>
              </>
            )}
            <div>
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid={AUTH.emailInput} className="mt-1.5 rounded-sm bg-secondary/40" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid={AUTH.passwordInput} minLength={6} className="mt-1.5 rounded-sm bg-secondary/40" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-sm bg-primary hover:bg-primary/90 text-white font-semibold h-11 active:scale-[0.98] transition-transform" data-testid={AUTH.submitButton}>
              {loading ? "…" : mode === "login" ? "Sign in →" : "Create workspace →"}
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid={AUTH.switchModeButton}
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              {mode === "login" ? "Need an account? Register" : "Already have an account? Sign in"}
            </button>
            {mode === "login" && (
              <a href="/forgot-password" data-testid="login-forgot-password-link" className="text-muted-foreground hover:text-primary transition-colors">Forgot password?</a>
            )}
          </div>

          {mode === "login" && (
            <div className="mt-6 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold text-center pt-6 border-t border-border">One-click demo access</div>
              <div className="grid grid-cols-3 gap-2 pt-1">
                <QuickLogin label="Company Admin" role="Full workspace" email="admin@leadifycrm.com" password="Admin@2026" setEmail={setEmail} setPassword={setPassword} setLoading={setLoading} loading={loading} nav={nav} login={login} testId="quick-admin" />
                <QuickLogin label="Salesperson" role="Agent view" email="sales@leadifycrm.com" password="Sales@2026" setEmail={setEmail} setPassword={setPassword} setLoading={setLoading} loading={loading} nav={nav} login={login} testId="quick-sales" />
                <QuickLogin label="Super Admin" role="Cross-company" email="super@leadifycrm.com" password="Super@2026" setEmail={setEmail} setPassword={setPassword} setLoading={setLoading} loading={loading} nav={nav} login={login} testId="quick-super" />
              </div>
            </div>
          )}
        </Card>
        <div className="text-center text-[11px] text-muted-foreground mt-6 flex items-center justify-center gap-4" data-testid="login-legal-links">
          <a href="/privacy" className="hover:text-primary transition-colors">Privacy</a>
          <span>·</span>
          <a href="/terms" className="hover:text-primary transition-colors">Terms</a>
          <span>·</span>
          <a href="mailto:info@leadifycrm.com" className="hover:text-primary transition-colors">Contact</a>
        </div>
      </div>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display text-3xl font-extrabold tracking-tight">{n}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mt-1">{label}</div>
    </div>
  );
}

function QuickLogin({ label, role, email, password, setEmail, setPassword, setLoading, loading, nav, login, testId }) {
  const go = async () => {
    setEmail(email);
    setPassword(password);
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      // toast happens outside
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={go}
      disabled={loading}
      data-testid={testId}
      className="p-3 rounded-sm border border-border bg-secondary/20 hover:border-primary hover:bg-secondary/60 transition-colors text-left group disabled:opacity-50"
    >
      <div className="text-[10px] uppercase tracking-[0.15em] text-primary font-bold group-hover:text-white">{label}</div>
      <div className="text-[10px] text-muted-foreground mt-1 leading-tight">{role}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-primary mt-2">Sign in →</div>
    </button>
  );
}
